import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

// Mock API limits data
const mockAPILimits = [
  {
    service: 'OpenRouter',
    current: 850,
    limit: 1000,
    period: 'minute',
    resetTime: '45s',
    status: 'warning'
  },
  {
    service: 'Anthropic',
    current: 23,
    limit: 50,
    period: 'minute',
    resetTime: '12s',
    status: 'healthy'
  },
  {
    service: 'Vapi',
    current: 89,
    limit: 100,
    period: 'minute',
    resetTime: '23s',
    status: 'warning'
  },
  {
    service: 'OpenAI',
    current: 1950,
    limit: 2000,
    period: 'minute',
    resetTime: '8s',
    status: 'critical'
  }
];

interface APILimitCardProps {
  service: string;
  current: number;
  limit: number;
  period: string;
  resetTime: string;
  status: 'healthy' | 'warning' | 'critical';
}

function APILimitCard({ service, current, limit, period, resetTime, status }: APILimitCardProps) {
  const percentage = (current / limit) * 100;
  
  const getStatusConfig = () => {
    switch (status) {
      case 'healthy':
        return {
          icon: <CheckCircle className="w-4 h-4 text-green-500" />,
          color: '#10b981',
          badge: 'default'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
          color: '#f59e0b',
          badge: 'secondary'
        };
      case 'critical':
        return {
          icon: <XCircle className="w-4 h-4 text-red-500" />,
          color: '#ef4444',
          badge: 'destructive'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Card className="hover:shadow-sm transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{service}</CardTitle>
          <div className="flex items-center gap-2">
            {config.icon}
            <Badge variant={config.badge as any} className="text-xs">
              {status}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span>Usage</span>
            <span className="font-mono">
              {current.toLocaleString()} / {limit.toLocaleString()}
            </span>
          </div>
          
          <Progress 
            value={percentage} 
            className="h-2"
            style={{
              background: `linear-gradient(to right, ${config.color} 0%, ${config.color} ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
            }}
          />
          
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Per {period}</span>
            <span>Resets in {resetTime}</span>
          </div>
          
          <div className="text-xs">
            <span className="text-muted-foreground">Rate: </span>
            <span className="font-mono">{percentage.toFixed(1)}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function APILimitsMonitor() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {mockAPILimits.map((limit) => (
          <APILimitCard key={limit.service} {...limit} />
        ))}
      </div>
      
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">API Usage Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {mockAPILimits.filter(l => l.status === 'healthy').length}
              </div>
              <div className="text-sm text-muted-foreground">Healthy</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">
                {mockAPILimits.filter(l => l.status === 'warning').length}
              </div>
              <div className="text-sm text-muted-foreground">Warning</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {mockAPILimits.filter(l => l.status === 'critical').length}
              </div>
              <div className="text-sm text-muted-foreground">Critical</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}